// CWA (Center Weather Advisory) layer family.
//
// Per-ARTCC short-fuse weather advisories (2–3 hr validity). Often empty,
// occasionally critical (sudden hazards within an ARTCC). Driven by the
// wx-hazards bar's CWA chip. One GeoJSON source drives two layers: a
// hazard-coded fill (0.30 opacity) and a solid hazard-coded border line.
// Both layers are mounted with visibility: 'none' (lazy toggle).
//
// Note: addOverlays.ts also calls applyValidityWindowFilter on these layers
// immediately after mount (inside the `if (!map.getSource('cwa'))` guard)
// to seed the time-scrubber state. That call stays in addOverlays.ts because
// it depends on the wxHazardOffsetH store value at mount time and is not
// part of the per-family LayerFamilySpec contract. The applyState here
// handles visibility toggling only.
//
// Extracted from addOverlays.ts as HI-1 batch 7 round 2 (2026-05-24).
// Pure mechanical move — source spec, paint expressions, layer ids, and
// layer order are byte-identical to the pre-extraction form.
//
// applyState reads visibility from useUiStore so a basemap setStyle()
// rebuild restores the user's current toggle state transparently.

import type { Map as MlMap } from 'maplibre-gl';
import { EMPTY_FC } from '../MapView';
import { registerLayerFamily } from '../layerVisibilityRegistry';
import { useLayerVisibilityStore } from '../../stores/layerVisibilityStore';
import { useUiStore } from '../../stores/uiStore';
import { applyValidityWindowFilter } from '../mapFilters';
import { isOverlayLoaded, markOverlayLoaded, refreshOverlay } from '../overlayRuntime';
import { OVERLAY_BY_ID } from '../overlaySpecs';

export function addCwaLayers(map: MlMap): void {
  if (!map.getSource('cwa')) {
    map.addSource('cwa', { type: 'geojson', data: EMPTY_FC });
    map.addLayer({
      id: 'cwa-fill',
      type: 'fill',
      source: 'cwa',
      layout: { visibility: 'none' },
      paint: {
        // CWAs carry a 'hazard' code similar to G-AIRMET. Color by
        // hazard so the type is recognizable at a glance.
        'fill-color': [
          'match', ['get', 'hazard'],
          'IFR',     '#60a5fa',
          'TURB',    '#fb923c',
          'ICE',     '#7dd3fc',
          'CONVECTIVE', '#dc2626',
          'MT_OBSC', '#a855f7',
          /* default */ '#f472b6', // pink for 'OTHER' / unknown CWA codes
        ],
        'fill-opacity': 0.30,
      },
    });
    map.addLayer({
      id: 'cwa-line',
      type: 'line',
      source: 'cwa',
      layout: { visibility: 'none' },
      paint: {
        'line-color': [
          'match', ['get', 'hazard'],
          'IFR',     '#2563eb',
          'TURB',    '#ea580c',
          'ICE',     '#0284c7',
          'CONVECTIVE', '#b91c1c',
          'MT_OBSC', '#7e22ce',
          /* default */ '#db2777',
        ],
        'line-width': 2.5,
        // Solid line distinguishes CWA from FZLVL's dashed and AIRMET
        // turb's dashed.
      },
    });
  }
}

registerLayerFamily({
  id: 'cwa',
  label: 'Center Weather Advisories',
  sourceId: 'cwa',
  layerIds: ['cwa-fill', 'cwa-line'],
  mount: (map) => addCwaLayers(map),
  visibilitySelector: (l) => l.wxhazards && l.cwa,
  applyState: (map) => {
    if (!map.getLayer('cwa-fill')) return;
    const layers = useLayerVisibilityStore.getState().layers;
    const show = layers.wxhazards && layers.cwa;
    const v = show ? 'visible' : 'none';
    map.setLayoutProperty('cwa-fill', 'visibility', v);
    map.setLayoutProperty('cwa-line', 'visibility', v);
    // Re-apply validity window filter on basemap-rebuild + slider change.
    if (show) {
      const offsetH = useUiStore.getState().wxHazardOffsetH;
      applyValidityWindowFilter(map, ['cwa-fill', 'cwa-line'], offsetH);
    }
  },
  onToggleOn: (map) => {
    const spec = OVERLAY_BY_ID.get('cwa');
    if (!spec || isOverlayLoaded('cwa')) return;
    markOverlayLoaded('cwa');
    return refreshOverlay(map, spec);
  },
});
